import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface WeeklyPlanItem {
  id: string;
  user_id: string;
  plan_date: string;
  item_type: 'workout' | 'meal' | 'sleep' | 'rest_day';
  title: string;
  description: string | null;
  workout_program_day_id: string | null;
  workout_template_id: string | null;
  saved_meal_id: string | null;
  saved_recipe_id: string | null;
  meal_slot: string | null;
  sleep_time: string | null;
  wake_time: string | null;
  scheduled_time: string | null;
  sort_order: number;
  is_completed: boolean;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export type NewPlanItem = Omit<WeeklyPlanItem, 'id' | 'user_id' | 'created_at' | 'updated_at'>;

export const useWeeklyPlan = (startDate: string, endDate: string) => {
  const { user } = useAuth();
  const [items, setItems] = useState<WeeklyPlanItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchItems = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('weekly_plan_items')
        .select('*')
        .eq('user_id', user.id)
        .gte('plan_date', startDate)
        .lte('plan_date', endDate)
        .order('plan_date')
        .order('sort_order');

      if (error) throw error;
      setItems((data as WeeklyPlanItem[]) || []);
    } catch (error) {
      console.error('Error fetching weekly plan:', error);
    } finally {
      setLoading(false);
    }
  }, [user, startDate, endDate]);

  const addItem = async (item: Partial<NewPlanItem> & Pick<NewPlanItem, 'plan_date' | 'item_type' | 'title'>) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('weekly_plan_items')
        .insert({ ...item, user_id: user.id });
      if (error) throw error;
      toast.success('Plan item added');
      await fetchItems();
    } catch (error) {
      console.error('Error adding plan item:', error);
      toast.error('Failed to add plan item');
    }
  };

  const updateItem = async (id: string, updates: Partial<NewPlanItem>) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('weekly_plan_items')
        .update(updates)
        .eq('id', id)
        .eq('user_id', user.id);
      if (error) throw error;
      toast.success('Plan updated');
      await fetchItems();
    } catch (error) {
      console.error('Error updating plan item:', error);
      toast.error('Failed to update plan item');
    }
  };

  const deleteItem = async (id: string) => {
    if (!user) return;
    try {
      const { error } = await supabase
        .from('weekly_plan_items')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);
      if (error) throw error;
      toast.success('Plan item removed');
      await fetchItems();
    } catch (error) {
      console.error('Error deleting plan item:', error);
      toast.error('Failed to remove plan item');
    }
  };

  const toggleComplete = async (id: string, completed: boolean) => {
    await updateItem(id, {
      is_completed: completed,
      completed_at: completed ? new Date().toISOString() : null,
    });
  };

  useEffect(() => {
    if (user) fetchItems();
  }, [user, fetchItems]);

  return { items, loading, addItem, updateItem, deleteItem, toggleComplete, refetch: fetchItems };
};
